import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useState } from "react";
import { useAppContext } from "../../AppContext";
import { UpdateUserGroup, UserGroupEmail, UserGroupSaveResponse } from "../../models/userGroup";
import { updateUserGroup } from "../../services/userGroupService";
import { getInvitedEmailsMessage } from "../../utils/userGroupInvite";
import NotificationDialog from "./NotificationDialog";

type UserGroupAssignment = {
  user: { userID: number; email: string };
  roles?: { roleID: number; roleName: string }[];
};

type UserGroup = {
  userGroupID: number;
  userGroupName: string;
  userGroupAssignments: UserGroupAssignment[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  userGroup: UserGroup;
  onUserInvited?: () => void;
};

const InviteUserToGroupDialog = (props: Props) => {
  const { user } = useAppContext();
  const [email, setEmail] = useState("");
  const [isMember, setIsMember] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [saving, setSaving] = useState(false);
  const [invitedEmailsMessage, setInvitedEmailsMessage] = useState<string | null>(null);

  // Ramavtalsansvarig can't add plain Kommunanvändare unless they're already behörighetsansvarig in this group
  const isRamavtalsansvarig = user?.roles?.some((r: any) => r.roleName === 'Ramavtalsansvarig') ?? false;
  const isBehorighetsansvarigInGroup = props.userGroup.userGroupAssignments.some((a) => a.user.userID === user?.user?.userID && a.roles?.some((r) => r.roleName === 'Behörighetsansvarig')) ?? false;
  const restrictToCoordinatorOnly = isRamavtalsansvarig && !isBehorighetsansvarigInGroup;

  const isValidEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const canSubmit = email.trim().length > 0 && (isMember || isAdmin);

  const handleInvite = async () => {
    if (!isValidEmail(email)) {
      setEmailError("Värdet är inte en giltig e-postadress");
      return;
    }

    const existingEmails: UserGroupEmail[] =
      props.userGroup.userGroupAssignments.map((a) => ({
        Email: a.user.email,
        IsAdmin: a.roles?.some((r) => r.roleName === 'Behörighetsansvarig') ?? false,
      }));

    if (existingEmails.some((e) => e.Email === email)) {
      setEmailError("Användaren finns redan i gruppen");
      return;
    }

    const newEntries: UserGroupEmail[] = [];
    if (isMember) newEntries.push({ Email: email, IsAdmin: false });
    if (isAdmin) newEntries.push({ Email: email, IsAdmin: true });

    const submitData: UpdateUserGroup = {
      UserGroupID: props.userGroup.userGroupID,
      Emails: [...existingEmails, ...newEntries],
    };

    setSaving(true);
    try {
      const result: UserGroupSaveResponse = await updateUserGroup(submitData);
      props.onUserInvited?.();
      handleClose();
      if (result?.invitedEmails && result.invitedEmails.length > 0) {
        setInvitedEmailsMessage(getInvitedEmailsMessage(result.invitedEmails));
      }
    } catch (err) {
      console.error("Error inviting user:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setIsMember(false);
    setIsAdmin(false);
    setEmailError("");
    props.onClose();
  };

  return (
    <>
    <Dialog
      open={props.open}
      onOpenChange={(_e, data) => !data.open && handleClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                shape="circular"
                appearance="subtle"
                aria-label="Stäng"
                icon={<DismissRegular />}
                onClick={handleClose}
              />
            }
          >
            Lägg till medarbetare
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4 pt-2">
              <Field
                label="Lägg till användare (E-post):"
                validationMessage={emailError}
                validationState={emailError ? "error" : "none"}
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(_, data) => {
                    setEmail(data.value);
                    setEmailError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleInvite();
                  }}
                  placeholder="namn@organisation.se"
                />
              </Field>
              <div className="flex flex-col gap-1">
                <Text weight="semibold">Välj roll:</Text>
                <div className="flex flex-col gap-1 pt-1">
                  {!restrictToCoordinatorOnly && (
                    <Checkbox
                      label="Kommunanvändare"
                      checked={isMember}
                      onChange={(_, data) => setIsMember(!!data.checked)}
                    />
                  )}
                  <Checkbox
                    label="Behörighetsansvarig"
                    checked={isAdmin}
                    onChange={(_, data) => setIsAdmin(!!data.checked)}
                  />
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={saving}>
              Avbryt
            </Button>
            <Button
              appearance="primary"
              onClick={handleInvite}
              disabled={saving || !canSubmit}
              icon={saving ? <Spinner size="tiny" /> : undefined}
            >
              {saving ? "Lägger till..." : "Lägg till"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    <NotificationDialog
      isOpen={!!invitedEmailsMessage}
      message={invitedEmailsMessage ?? ""}
      onClose={() => setInvitedEmailsMessage(null)}
    />
    </>
  );
};

export default InviteUserToGroupDialog;
